import React from 'react';
import { useSelector } from 'react-redux';

import Table from 'components/Table';

import { getPValue } from 'utilities';
import { round6 } from 'utilities/math';

import {
  getAltHypotesisSign,
  getH0,
  getH1,
  getTableData,
  getNumericalRowDataSet,
  getPValue as getPValueFromStore,
  getTStatistics,
  getStandardError,
  getDF,
  getMeanOfSampleDiff,
  getPairedTTwoGroups
} from 'store/scenes/inferentialStatistics/selectors';
import { getVisibleNumericalSelectedParametersNames } from 'store/dataset';

import { getTableHeadersForPairedT } from '../shared';

const PairedTHypothesis = () => {
  const altHypothesisSign = useSelector(state => getAltHypotesisSign(state));
  const h0 = useSelector(state => getH0(state));
  const h1 = useSelector(state => getH1(state));
  const tableData = useSelector(state => getTableData(state));
  const tStatistics = useSelector(state => getTStatistics(state));
  const pValue = useSelector(state => getPValueFromStore(state));
  const standardError = useSelector(state => getStandardError(state));
  const df = useSelector(state => getDF(state));
  const isDataSetAvailable = useSelector(state => getVisibleNumericalSelectedParametersNames(state).length > 1 && getPairedTTwoGroups(state) || false);
  const meanOfSampleDiff = useSelector(state => getMeanOfSampleDiff(state));
  const numericalRowDataSet = useSelector(state => getNumericalRowDataSet(state));

  const tableHeaders = getTableHeadersForPairedT(isDataSetAvailable, !!numericalRowDataSet);

  return (
    <div className="content-item">
      <h3 className="mb-20">Paired <i>t</i> Hypothesis Test Summary</h3>
      <div className="mb-5 pl-5 font-900">
        {isDataSetAvailable && (
          <p className="mb-5">
            Differences = x<sub>1</sub> - x<sub>2</sub>
          </p>
        )}
        <p>
          μ<sub><i>d</i></sub>{' '}
          : mean of population differences
        </p>
      </div>
      <table className="infStatMarkup-table">
        <tbody>
          <tr>
            <td>Null Hypothesis</td>
            <td>
              <span className="font-900 font-16 mb-10 mr-5">
                H<sub>0</sub>: μ
                <sub>
                  <i>d</i>
                </sub>{' '}
                =
              </span>
              <span>{round6(h0)}</span>
            </td>
          </tr>
          <tr>
            <td className="pb-20">Alternative Hypothesis</td>
            <td className="pb-20">
              <span className="font-900 font-16 mb-10 mr-5">
                H<sub>1</sub>: μ
                <sub>
                  <i>d</i>
                </sub>{' '}
                {altHypothesisSign}
              </span>
              <span>{round6(h1)}</span>
            </td>
          </tr>
          {isDataSetAvailable && (
            <tr>
              <td>Mean of Sample Differences</td>
              <td>{round6(meanOfSampleDiff)}</td>
            </tr>)
          }
          <tr>
            <td>T-Statistic</td>
            <td>
              <span>{round6(tStatistics)}</span>
            </td>
          </tr>
          <tr>
            <td>P-Value</td>
            <td>
              <span>{getPValue(pValue)}</span>
            </td>
          </tr>
          <tr>
            <td>df </td>
            <td>
              <span>{round6(df)}</span>
            </td>
          </tr>
          {isDataSetAvailable && (
            <tr>
              <td>Standard Error</td>
              <td>{round6(standardError)}</td>
            </tr>)
          }
        </tbody>
      </table>
      <h3>Descriptive Statistics of Sample</h3>
      <Table tableHeaders={tableHeaders} tableData={tableData} />
    </div>
  );
};

export default PairedTHypothesis;
